/**
 * Utility functions for timezone conversion and formatting
 * Converts datetime to IST (Indian Standard Time) and formats it
 * 
 * This utility handles:
 * - Image created_at timestamps from JobCardImage API
 * - Jobcard created_at timestamps from JobCard API
 * - Converts any timezone to IST (Asia/Kolkata)
 * - Formats as "DD MMM YYYY, HH:MM" (date then time with hours and minutes)
 */

/**
 * Formats datetime to IST and returns as "DD MMM YYYY, HH:MM" format
 * @param {string|Date} dateTime - The datetime string or Date object from API
 * @returns {string} - Formatted string like "25 Dec 2024, 14:30" or null if invalid
 */
export const formatToIST = (dateTime) => {
  if (!dateTime) return null;

  try {
    const date = new Date(dateTime);
    
    if (isNaN(date.getTime())) {
      console.warn('Invalid date provided:', dateTime);
      return null;
    }

    // Use Intl.DateTimeFormat to format directly in IST timezone
    const formatter = new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    
    // Extract parts
    const day = parts.find(p => p.type === 'day').value.padStart(2, '0');
    const month = parts.find(p => p.type === 'month').value;
    const year = parts.find(p => p.type === 'year').value;
    const hour = parts.find(p => p.type === 'hour').value.padStart(2, '0');
    const minute = parts.find(p => p.type === 'minute').value.padStart(2, '0');

    return `${day} ${month} ${year}, ${hour}:${minute}`;
  } catch (error) {
    console.error('Error formatting date to IST:', error);
    return null;
  }
};

/**
 * Formats image created_at timestamp to IST
 * @param {string|Date} imageCreatedAt - The created_at timestamp from JobCardImage API
 * @returns {string} - Formatted string in IST like "25 Dec 2024, 14:30" or null if invalid
 */
export const formatImageCreatedAt = (imageCreatedAt) => {
  return formatToIST(imageCreatedAt);
};

/**
 * Formats jobcard created_at timestamp to IST
 * @param {string|Date} jobcardCreatedAt - The created_at timestamp from JobCard API
 * @returns {string} - Formatted string in IST like "25 Dec 2024, 14:30" or null if invalid
 */
export const formatJobcardCreatedAt = (jobcardCreatedAt) => {
  return formatToIST(jobcardCreatedAt);
};

